import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Mail,
    ShoppingBag,
    Phone,
    Send,
    MessageCircle,
    Instagram,
    Loader2,
    Smartphone,
    Headset,
    Settings as Cog,
    X,
    Code2,
    Copy,
    Check,
} from "lucide-react";
import { adminApi, API_BASE, getActiveAgent } from "@/lib/api";
import { toast } from "sonner";
import { ORCHID_LOGO } from "@/lib/brand";

const AGENT_LABEL = { priya: "Priya", tara: "Tara" };

function InstallSnippet() {
    const [copied, setCopied] = useState(false);
    const [agents, setAgents] = useState([]);
    const agentId = getActiveAgent();

    useEffect(() => {
        adminApi.get("/api/admin/bot-agents").then((r) => setAgents(r.data.items || [])).catch(() => {});
    }, []);

    const agent = agents.find((a) => a.id === agentId);
    const name = agent?.name || AGENT_LABEL[agentId] || agentId;
    const snippet = `<!-- Orchid ${name} chat widget -->\n<script src="${API_BASE}/api/widget.js?agent=${agentId}" async></script>`;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(snippet);
        } catch {
            const t = document.createElement("textarea");
            t.value = snippet;
            document.body.appendChild(t);
            t.select();
            document.execCommand("copy");
            document.body.removeChild(t);
        }
        setCopied(true);
        toast.success(`${name}'s install snippet copied`);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div
            className="mb-6 bg-white border border-stone-200 rounded-xl p-6"
            data-testid="install-snippet"
            style={{ borderLeft: `3px solid ${agent?.accent || "#7E2A6C"}` }}
        >
            <div className="flex items-center gap-2 mb-1">
                <Code2 size={18} className="text-orchid-700" />
                <h2 className="font-heading text-xl text-stone-900">Install {name} on your website</h2>
            </div>
            <p className="text-xs text-stone-500 mb-4">
                Paste this single line just before <code className="bg-stone-100 px-1 rounded">&lt;/body&gt;</code> on
                every page where you want the <strong>{name}</strong> chat bubble to appear. Each agent has its own
                snippet — switch agents (top-left) to get the other one.
            </p>
            <div className="relative">
                <pre
                    className="bg-stone-900 text-stone-100 text-xs rounded-lg p-4 pr-14 overflow-x-auto leading-relaxed"
                    data-testid="install-snippet-code"
                >
{snippet}
                </pre>
                <button
                    onClick={copy}
                    data-testid="install-snippet-copy"
                    className="absolute top-3 right-3 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-white/10 text-white hover:bg-white/20 transition-colors"
                >
                    {copied ? <Check size={13} /> : <Copy size={13} />}
                    {copied ? "Copied" : "Copy"}
                </button>
            </div>
        </div>
    );
}

// === Hero channels (large mockup cards) ===
const HERO = [
    {
        key: "chat_widget",
        title: "Chat widget",
        description: "Add a floating chat window to your site.",
        cta: { label: "Manage", to: "/settings/chat-interface" },
        active: true,
        canToggle: true,
    },
    {
        key: "help_page",
        title: "Help page",
        description: "ChatGPT-style help page, deployed standalone or under a path on your site (/help).",
        cta: { label: "Setup", to: "/deployment" },
    },
];

// === Integration channels (locked) ===
const CHANNELS = [
    {
        key: "email",
        title: "Email",
        description: "Connect your agent to an email address and let it respond to messages from your customers.",
        icon: Mail,
        accent: "bg-rose-500/15 text-rose-600",
    },
    {
        key: "shopify",
        title: "Shopify",
        description: "Connect your agent to Shopify and let it respond to messages from your customers.",
        icon: ShoppingBag,
        accent: "bg-emerald-500/15 text-emerald-600",
    },
    {
        key: "phone",
        title: "Phone",
        badge: "Beta",
        description: "Let your AI agent handle inbound phone calls.",
        icon: Phone,
        accent: "bg-violet-500/15 text-violet-600",
    },
    {
        key: "whatsapp",
        title: "WhatsApp",
        description: "Connect your agent to a WhatsApp number and let it respond automatically.",
        icon: Send,
        accent: "bg-emerald-600/15 text-emerald-700",
    },
    {
        key: "messenger",
        title: "Messenger",
        description: "Connect your agent to a Facebook page and let it respond to inbound messages.",
        icon: MessageCircle,
        accent: "bg-sky-500/15 text-sky-600",
    },
    {
        key: "instagram",
        title: "Instagram",
        description: "Connect your agent to an Instagram page and let it respond to inbound DMs.",
        icon: Instagram,
        accent: "bg-pink-500/15 text-pink-600",
    },
];

export default function Deploy() {
    const navigate = useNavigate();
    const [active, setActive] = useState({ chat_widget: true });
    const [lc, setLc] = useState(null);
    const [lcSaving, setLcSaving] = useState(false);
    const lcDirty = useRef(false); // once user toggles, ignore late/duplicate mount fetches
    const [requestOpen, setRequestOpen] = useState(null); // channel key
    const [form, setForm] = useState({ name: "", email: "", notes: "" });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        adminApi
            .get("/api/admin/deploy/channels")
            .then((r) => setActive(r.data.channels || { chat_widget: true }))
            .catch(() => {});
        adminApi
            .get("/api/admin/settings/live-chat")
            .then((r) => { if (!lcDirty.current) setLc(r.data); })
            .catch(() => {});
    }, []);

    const toggleWidget = async () => {
        const next = { ...active, chat_widget: !active.chat_widget };
        setActive(next);
        try {
            await adminApi.put("/api/admin/deploy/channels", { channels: next });
            toast.success(
                next.chat_widget
                    ? "Chat widget shown on your website"
                    : "Chat widget hidden site-wide — the whole bot is now off"
            );
        } catch (err) {
            setActive(active);
            toast.error("Failed to update");
        }
    };

    const toggleLiveChat = async () => {
        if (!lc || lcSaving) return;
        lcDirty.current = true;
        const next = !lc.live_chat_enabled;
        setLcSaving(true);
        setLc({ ...lc, live_chat_enabled: next });
        try {
            await adminApi.put("/api/admin/settings/live-chat", { enabled: next });
            toast.success(
                next
                    ? `Live Agent enabled for ${lc.name}`
                    : `Live Agent turned off — ${lc.name} keeps chatting, the bot stays live`
            );
        } catch (err) {
            setLc({ ...lc, live_chat_enabled: !next });
            toast.error("Could not update Live Agent setting");
        } finally {
            setLcSaving(false);
        }
    };

    const requestAccess = async () => {
        setSubmitting(true);
        try {
            await adminApi.post("/api/admin/beta/request", {
                feature: `channel_${requestOpen}`,
                name: form.name,
                email: form.email,
                notes: form.notes,
            });
            toast.success("Request received — our team will reach out within 1 business day.");
            setRequestOpen(null);
            setForm({ name: "", email: "", notes: "" });
        } catch (err) {
            toast.error("Failed to submit");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="p-10 max-w-6xl" data-testid="page-deploy">
            <h1 className="font-heading text-4xl text-stone-900 mb-6">All channels</h1>

            <InstallSnippet />

            {/* Hero: Chat widget + Help page */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <HeroChatWidget
                    active={active.chat_widget}
                    onToggle={toggleWidget}
                    onManage={() => navigate("/settings/chat-interface")}
                />
                <HeroHelpPage onSetup={() => navigate("/deployment")} />
            </div>

            {/* Live Agent (human handoff) on/off — the OFF-HOURS switch */}
            {lc && (
                <LiveAgentToggle lc={lc} onToggle={toggleLiveChat} saving={lcSaving} />
            )}

            {/* Integration grid */}
            <div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                data-testid="channels-grid"
            >
                {CHANNELS.map((c) => (
                    <div
                        key={c.key}
                        className="bg-white border border-stone-200 rounded-xl p-5"
                        data-testid={`channel-${c.key}`}
                    >
                        <div className="flex items-start justify-between mb-4">
                            <div
                                className={`w-12 h-12 rounded-xl flex items-center justify-center ${c.accent}`}
                            >
                                <c.icon size={22} />
                            </div>
                            {c.badge && (
                                <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded bg-stone-900 text-white">
                                    {c.badge}
                                </span>
                            )}
                        </div>
                        <div className="font-medium text-stone-900 text-base mb-1">{c.title}</div>
                        <p className="text-xs text-stone-600 leading-relaxed mb-4 min-h-[36px]">
                            {c.description}
                        </p>
                        <div className="flex items-center justify-between">
                            <button
                                title="Mobile-only setup"
                                className="p-1.5 text-stone-400 hover:text-stone-700 rounded"
                            >
                                <Smartphone size={14} />
                            </button>
                            <button
                                onClick={() => {
                                    setRequestOpen(c.key);
                                    setForm({ name: "", email: "", notes: "" });
                                }}
                                data-testid={`channel-${c.key}-upgrade`}
                                className="text-xs font-medium px-4 py-1.5 rounded-md border border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white hover:border-stone-900 transition-colors"
                            >
                                Upgrade to enable
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Request-access modal */}
            {requestOpen && (
                <div
                    className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                    onClick={() => setRequestOpen(null)}
                    data-testid="request-modal"
                >
                    <div
                        className="bg-white rounded-xl w-full max-w-md p-6 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between mb-2">
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-stone-500">
                                    Request access
                                </div>
                                <h3 className="font-heading text-2xl text-orchid-700 capitalize">
                                    {requestOpen.replace("_", " ")}
                                </h3>
                            </div>
                            <button
                                onClick={() => setRequestOpen(null)}
                                className="text-stone-400 hover:text-stone-700"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <p className="text-xs text-stone-500 mb-5">
                            This channel is included with the Pro / Enterprise plan. Tell us a bit about your use case and our team will reach out within 1 business day.
                        </p>
                        <div className="space-y-3">
                            <input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Your name"
                                data-testid="request-name"
                                className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                            />
                            <input
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                placeholder="Your email"
                                type="email"
                                data-testid="request-email"
                                className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                            />
                            <textarea
                                rows={3}
                                value={form.notes}
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                placeholder="What do you want to use this channel for?"
                                data-testid="request-notes"
                                className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                            />
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button
                                onClick={() => setRequestOpen(null)}
                                className="text-sm px-4 py-2 rounded-md border border-stone-200 text-stone-700 hover:bg-stone-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={requestAccess}
                                disabled={submitting}
                                data-testid="request-submit"
                                className="text-sm font-medium px-4 py-2 rounded-md bg-orchid-700 hover:bg-orchid-600 text-white disabled:opacity-50 inline-flex items-center gap-2"
                            >
                                {submitting ? (
                                    <Loader2 size={13} className="animate-spin" />
                                ) : null}
                                Request access
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function LiveAgentToggle({ lc, onToggle, saving }) {
    const on = lc.live_chat_enabled !== false;
    return (
        <div
            className="bg-white border border-stone-200 rounded-xl p-6 mb-6 flex items-start justify-between gap-6"
            data-testid="deploy-live-agent-card"
        >
            <div className="flex gap-4">
                <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-emerald-500/15 text-emerald-600 flex items-center justify-center">
                    <Headset size={20} />
                </div>
                <div>
                    <div className="font-medium text-stone-900 mb-1">
                        Live Agent (human handoff) — {lc.name}
                    </div>
                    <p className="text-sm text-stone-600 max-w-xl">
                        Turn this <strong>off during off-hours</strong> so guests aren&apos;t offered a
                        human agent when your team is offline. {lc.name} keeps answering as normal —
                        this does <strong>not</strong> hide the chatbot.
                    </p>
                    <p className="text-xs text-stone-500 mt-2">
                        Status:{" "}
                        <strong
                            className={on ? "text-emerald-600" : "text-stone-500"}
                            data-testid="deploy-live-agent-status"
                        >
                            {on ? "Live Agent ON" : "Live Agent OFF"}
                        </strong>
                    </p>
                </div>
            </div>
            <button
                onClick={onToggle}
                disabled={saving}
                role="switch"
                aria-checked={on}
                data-testid="deploy-live-agent-toggle"
                className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${
                    on ? "bg-emerald-500" : "bg-stone-300"
                }`}
            >
                <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${
                        on ? "translate-x-5" : "translate-x-0.5"
                    }`}
                />
            </button>
        </div>
    );
}

function HeroChatWidget({ active, onToggle, onManage }) {
    return (
        <div
            className="bg-white border border-stone-200 rounded-xl overflow-hidden"
            data-testid="channel-chat_widget"
        >
            <div className="h-56 bg-gradient-to-br from-sky-400 to-blue-500 relative overflow-hidden flex items-center justify-center">
                <div className="absolute bottom-0 right-4 w-44 h-40 bg-white rounded-t-xl shadow-2xl overflow-hidden">
                    <div className="bg-orchid-700 text-white px-3 py-2 flex items-center gap-2">
                        <img
                            src={ORCHID_LOGO}
                            alt=""
                            className="h-6 w-6 bg-white rounded-full p-0.5"
                        />
                        <div className="text-[10px] font-medium">Orchid Hotels &amp; Resorts</div>
                    </div>
                    <div className="p-2 space-y-1.5">
                        <div className="bg-stone-100 rounded-xl rounded-bl-sm text-[9px] px-2 py-1.5 text-stone-700 max-w-[80%]">
                            Namaskar I&apos;m Priya! What can I help you with?
                        </div>
                    </div>
                </div>
            </div>
            <div className="p-5 flex items-start justify-between">
                <div className="flex-1">
                    <div className="font-medium text-stone-900 mb-1">Chat widget</div>
                    <p className="text-xs text-stone-600">
                        Show or hide the <strong>entire</strong> chat bubble on your website.
                        Turning this off removes the whole bot from your site.
                    </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    <button
                        onClick={onToggle}
                        data-testid="chat_widget-toggle"
                        aria-pressed={active}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            active ? "bg-emerald-500" : "bg-stone-300"
                        }`}
                    >
                        <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                                active ? "translate-x-5" : "translate-x-0.5"
                            }`}
                        />
                    </button>
                </div>
            </div>
            <div className="px-5 pb-5 flex items-center justify-between">
                <button
                    title="Mobile preview"
                    className="p-1.5 text-stone-400 hover:text-stone-700 rounded"
                >
                    <Smartphone size={14} />
                </button>
                <button
                    onClick={onManage}
                    data-testid="chat_widget-manage"
                    className="inline-flex items-center gap-1.5 text-xs font-medium px-4 py-1.5 rounded-md border border-stone-300 hover:bg-stone-50"
                >
                    <Cog size={12} /> Manage
                </button>
            </div>
        </div>
    );
}

function HeroHelpPage({ onSetup }) {
    return (
        <div
            className="bg-white border border-stone-200 rounded-xl overflow-hidden"
            data-testid="channel-help_page"
        >
            <div className="h-56 bg-gradient-to-br from-amber-400 via-amber-300 to-orange-400 relative overflow-hidden flex items-center justify-center">
                <div className="bg-white rounded-xl shadow-2xl w-3/4 max-w-xs">
                    <div className="px-3 py-2 border-b border-stone-100 flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-red-400" />
                        <span className="w-2 h-2 rounded-full bg-amber-400" />
                        <span className="w-2 h-2 rounded-full bg-emerald-400" />
                    </div>
                    <div className="p-4 text-center">
                        <div className="text-[11px] font-medium text-stone-900 mb-2">
                            How can we help you today?
                        </div>
                        <div className="h-10 bg-stone-100 rounded-md flex items-center px-2 text-[9px] text-stone-400">
                            Ask a question...
                        </div>
                    </div>
                </div>
            </div>
            <div className="p-5">
                <div className="font-medium text-stone-900 mb-1">Help page</div>
                <p className="text-xs text-stone-600 mb-4">
                    ChatGPT-style help page, deployed standalone or under a path on your site (/help).
                </p>
                <div className="flex items-center justify-end">
                    <button
                        onClick={onSetup}
                        data-testid="help_page-setup"
                        className="inline-flex items-center gap-1.5 text-xs font-medium px-4 py-1.5 rounded-md border border-stone-300 hover:bg-stone-50"
                    >
                        <Cog size={12} /> Setup
                    </button>
                </div>
            </div>
        </div>
    );
}
